import { useState } from 'react'
import { isEqual } from 'lodash'
import { DeepPartial, ExtractObjects } from 'react-hook-form'
import {
    CatalogTransferFilter,
    CatalogTransferFilterCollection,
    CatalogTransferStatusFilterType,
} from '@App/forms/filters/catalog-transfer-filter'
import CatalogTransferTabStore, {
    DEFAULT_FILTERS,
} from '@App/stores/rightsholder/catalogTransfer/catalog-transfer-tab-store'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'

export type CatalogTransferFilterFormValues =
    | (ExtractObjects<CatalogTransferFilter> extends never ? CatalogTransferFilter : DeepPartial<CatalogTransferFilter>)
    | undefined

export const useCatalogTransferFilters = (store: CatalogTransferTabStore, isReviewTab: boolean | undefined) => {
    const normalizeFilterValues = (filters: CatalogTransferFilterFormValues): CatalogTransferFilter => ({
        statusFilter: filters?.statusFilter?.filter(
            (status): status is CatalogTransferStatusFilterType => status != null
        ),
        sort_by: filters?.sort_by,
    })

    // Only the Review tab is ever linked to with filters preset (e.g. a dashboard card pointing at
    // "Submitted" transfers to review); the "My Submissions" tab's URL is left alone.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<CatalogTransferFilter>(
        CatalogTransferFilterCollection
    )
    const reviewTabUrlFilterValues = isReviewTab ? urlFilterValues : undefined

    // What this mount's filter should be: URL-seeded on Review (only when linked in that way), or
    // the plain defaults otherwise. `catalogTransferTabStore` is a session-persistent singleton, so
    // without this a stale filter applied on an earlier visit would otherwise still be showing (and
    // still be in effect) the next time a user lands here via ordinary navigation.
    const desiredFilter: CatalogTransferFilter = reviewTabUrlFilterValues
        ? {
              ...normalizeFilterValues(reviewTabUrlFilterValues),
              // A link rarely carries every filter (e.g. no sort): keep the default sort rather than dropping it.
              sort_by: reviewTabUrlFilterValues.sort_by ?? DEFAULT_FILTERS.sort_by,
          }
        : DEFAULT_FILTERS

    // Reconcile the store's filter with what this mount should show, once and before the first render (the search
    // key is built from it): seeded from the URL on Review when linked that way, or reset to plain defaults
    // otherwise — so a filter left over from an earlier visit this session never lingers into a fresh one. Results
    // fetched for the old filter are dropped, and so is the page it was on; the query refetches (or finds them
    // cached) for the new filter, from the first page.
    useState(() => {
        if (!isEqual(store.currentFilter, desiredFilter)) {
            store.reconcileFilter(reviewTabUrlFilterValues ? desiredFilter : undefined)
        }
    })

    return { desiredFilter, normalizeFilterValues, syncFiltersToUrl }
}
